<template>
  <v-dialog persistent max-width="900" v-model="showLoginPopup" overlay-opacity="0.7">

    <v-form @submit="relogin" id="re_login_fields">
      <ValidationObserver ref="ReLoginForm" v-slot="{ valid, pristine, reset }">
        <div class="d-flex elevation-6 flex-column"
             v-bind:style="{background: compColor.contentFrame}"
             style="border-radius: 3px;max-width: 900px;min-height:460px;">
          <div class="grow d-flex flex-column d-flex">

            <!-- HEADER  -->

            <div class="text-h6 font-weight-light pa-3 layout-column layout-align-center-center"
                 :class="isDarkMode? 'blue-grey--text text--lighten-2' : ' grey--text text--darken-2'">
              <v-subheader style="line-height: 30px;height: 30px;" class="display-1 font-weight-black pink--text text--darken-2">{{ $t('AUTH.APP_NAME') }}</v-subheader>
              <v-subheader style="line-height: 30px;height: 30px;" class="title font-weight-bold blue-grey--text text--darken-2">{{ $t('AUTH.RE_LOGIN.TITLE') }}</v-subheader>
            </div>

            <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

            <!-- CONTENT  -->

            <div class="ma-1 px-3 py-2 layout-column elevation-3" style="border-radius: 5px;overflow-y:auto;" :style="{'background': compColor.lxExpansion.background}">
              <div class="layout-row mx-5 my-3 pa-3 red accent-2 white--text layout-align-start-center"
                   style="border-left: 8px solid #d83b3b !important;border-radius: 3px;"
                   v-if="loginError && pristine">
                <v-avatar>
                  <v-icon dark large>error_outline</v-icon>
                </v-avatar>
                <div class="flex layout-column layout-align-start-center ml-2">
                  <p class="ma-0">{{ $t('AUTH.RE_LOGIN.WRONG_CREDENTIALS') }}</p>
                </div>
              </div>

              <v-alert
                v-if="!loginError || (loginError && !pristine)"
                border="left"
                prominent
                style="font-size:15px;"
                class="pa-3 my-3 mx-5"
                v-bind:class="{'light-blue lighten-4 blue-grey--text text--darken-2': compColor.isInDarkMode, 'blue-grey--text text--darken-1 blue lighten-5': !compColor.isInDarkMode}">
                <template slot="prepend">
                  <v-avatar>
                    <v-icon large>mdi-information-variant</v-icon>
                  </v-avatar>
                </template>
                {{ $t('AUTH.RE_LOGIN.HINT') }}
              </v-alert>

              <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>

              <div class="grow mt-5 pa-3">

                <div v-show="!twoFactor">
                  <v-text-field
                    :dark="compColor.isInDarkMode"
                    v-model="userName"
                    :color="compColor.primeColor"
                    prepend-inner-icon="supervised_user_circle"
                    :label="$t('AUTH.RE_LOGIN.USERNAME')"
                    outlined
                    class="mx-2"
                    disabled
                  ></v-text-field>

                  <validation-provider ref="Passwort" name="Passwort" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="model.password"
                      :dark="compColor.isInDarkMode"
                      :error-messages="errors[0]"
                      :color="compColor.primeColor"
                      prepend-inner-icon="fingerprint"
                      :label="$t('AUTH.RE_LOGIN.PASSWORD')"
                      placeholder=" "
                      autofocus
                      ref="passwordInput"
                      outlined
                      :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                      :type="showPassword ? 'text' : 'password'"
                      @click:append="showPassword = !showPassword"
                      class="mx-2"
                    ></v-text-field>
                  </validation-provider>
                </div>

                <div class="px-2" v-if="twoFactor">
                  <fake-input-frame label="Zweifaktorauthentifizierung" :data-fake-padding="'pa-4'">
                    <code-input v-model="model.passcode" ref="codeInputRelogin" :enter-func="relogin" :error-reset-func="resetErrors" :style-version="0"></code-input>
                  </fake-input-frame>
                </div>
              </div>
            </div>

            <!-- ACTIONS -->

            <v-divider v-bind:style="{'border-color':compColor.lightDivider}"></v-divider>
            <div class="layout-row flex pt-1 pb-2 pl-2 pr-1">
              <div class="grow mr-2" v-if="twoFactor">
                <lx-btn classes="mr-1 text-none"
                        block
                        :color="compColor.primeColor"
                        type="submit"
                        :label="$t('AUTH.RE_LOGIN.BUTTONS.TWO_FACTOR')"
                        :disabled="!model || !model.passcode || model.passcode.length < 6"
                        :tooltip="$t('AUTH.RE_LOGIN.BUTTONS.TWO_FACTOR_HINT')"></lx-btn>
              </div>
              <lx-btn classes="mr-1 text-none"
                      @click="changeUser()"
                      icon="mdi-account-switch"
                      :tooltip="$t('AUTH.RE_LOGIN.BUTTONS.CHANGE_USER')"></lx-btn>
              <div class="grow mr-1" v-if="!twoFactor">
                <lx-btn classes="mr-1 text-none"
                        type="submit"
                        block
                        :color="compColor.primeColor"
                        :label="$t('AUTH.RE_LOGIN.BUTTONS.LOGIN')"
                        :disabled="!valid"
                        :tooltip="$t('AUTH.RE_LOGIN.BUTTONS.LOGIN_HINT')"></lx-btn>
              </div>
              <lx-btn classes="mr-1 text-none"
                      @click="clearForm(reset)"
                      :disabled="pristine"
                      icon="clear"
                      :tooltip="$t('AUTH.RE_LOGIN.BUTTONS.CLEAR_HINT')"></lx-btn>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </v-form>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import CodeInput from './CodeInput'
import moment from 'moment'

const LOCAL_STORE = process.env.VUE_APP_BASE_NAME + '-trust-token'

export default Vue.component('ReLoginComponent', {
  name: 'ReLoginComponent',
  components: {
    CodeInput
  },
  data () {
    return {
      twoFactor: false,

      loginError: false,
      model: {},
      showPassword: false,

      username: '',
      loading: false,
      error: '',
    }
  },
  computed: {
    showLoginPopup () {
      this.resetErrors()

      return this.authStore.getters.showLoginPopup
    },
    userName () {
      return this.authStore.getters.getAuthUserName
    }
  },
  watch: {
    showLoginPopup (newValue) {
      if (newValue) {
        document.querySelector('.v-application--wrap').style.filter = 'blur(4px)'
      } else {
        document.querySelector('.v-application--wrap').style.filter = 'none'
      }
    }
  },
  methods: {
    relogin (ev) {
      if (this.loading) return

      ev.preventDefault() // wichtig damit Formular mit Enter abgeschickt werden kann

      this.loading = true

      let userFromStorage = this.authStore.getters.getAuthUserData
      this.model.login = userFromStorage.user.email

      let trusted = JSON.parse(window.localStorage.getItem(LOCAL_STORE))

      if (trusted && trusted.expire && trusted.token) {
        let timeNow = moment()
        let expireTime = moment(trusted.expire)

        if (expireTime.isAfter(timeNow)) {
          this.model.trust_token_id = trusted.token
        } else {
          window.localStorage.removeItem(LOCAL_STORE)
        }
      }

      axios.post('/login', this.model)
        .then(resLogin => {
          // Login in auth store
          this.authStore.dispatch('login', { data: resLogin.data, type: 'relogin' })
          this.model = {} // falls man sich in der anwendung erneut einloggen muss damit die name und pwd nicht vom letzten versuich drin stehen
          this.twoFactor = false
          this.resetErrors()

          if (this.$refs.codeInputRelogin) {
            this.$refs.codeInputRelogin.resetSingleFields()
          }

          if (this.$refs.ReLoginForm) {
            this.$refs.ReLoginForm.reset()
          }

          // load settings and update tabs when browser tab become visible
          this.$http.get('/users/settings')
            .then(res => {
              if (res.data && res.data.tabs && res.data.tabs.tabs) {
                this.setAllTabs(res.data.tabs.tabs)
              }
            })
            .catch(() => {})
        })
        .catch(error => {
          let errorMsg = error.message

          if (errorMsg.indexOf('406') >= 0) {
            this.twoFactor = true
            this.resetErrors()
          } else {
            this.error = errorMsg
            this.loginError = true

            if (this.$refs.ReLoginForm) {
              this.$refs.ReLoginForm.reset()
            }
            if (this.twoFactor) {
              this.model.passcode = null

              if (this.$refs.codeInputRelogin) {
                this.$refs.codeInputRelogin.resetSingleFields()
              }
            } else {
              this.model = {}
            }
          }
        })
        .finally(() => {
          this.loading = false
        })

    },
    resetErrors () {
      this.loginError = false
    },
    changeUser () {
      this.twoFactor = false
      this.logout()
    },
    clearForm (reset) {
      reset()
      this.twoFactor = false
      this.resetErrors()
      this.model = {}

      if (this.$refs.codeInputRelogin) {
        this.$refs.codeInputRelogin.resetSingleFields()
      }
    }
  }
})
</script>
